import { produce } from "immer";
import { BulkExportFormat, IDimension } from "../../types";
import type { BulkExportTab, IBulkExportContextState, IBulkExportReducerAction } from "./BulkExportProvider.types";
import { IsavedExportSettingsDTO } from "../../api";
import { FavouriteBulkExportItem } from "../../../favourites/types";

export function bulkExportReducer(state: IBulkExportContextState, action: IBulkExportReducerAction) {
  switch (action.type) {
    case "setDataSourceId": {
      const dataSourceId = action.payload as number;
      if (dataSourceId === state.dataSourceId) {
        return state;
      }
      return produce(state, (draft) => {
        draft.dataSourceId = dataSourceId;
        draft.dimensions = [];
        draft.defaultCalendarDimension = undefined;
      });
    }
    case "setSavedExportSettingsId": {
      const payloadObj = action.payload as { id?: number; dimensions?: IDimension[]; format: BulkExportFormat };
      return produce(state, (draft) => {
        draft.savedExportSettingsId = payloadObj.id;
        if (payloadObj.dimensions && state.currentSavedSettings && state.tab) {
          draft.dimensions = payloadObj.dimensions;
        }
        draft.format = payloadObj.format;
      });
    }
    case "setTab": {
      const tab = action.payload as BulkExportTab;
      if (state.tab === tab) {
        return state;
      }
      return produce(state, (draft) => {
        draft.tab = tab;
      });
    }
    case "setCubeId": {
      const cubeId = action.payload as string;
      return produce(state, (draft) => {
        draft.cubeId = cubeId;
      });
    }
    case "setFormat": {
      const format = action.payload as BulkExportFormat;
      if (format === state.format) {
        return state;
      }
      return produce(state, (draft) => {
        draft.format = format;
      });
    }
    case "setFormatCurrentFavourite": {
      const format = action.payload as any;
      const currentFormat = state.currentFavouriteSettings?.mark.parameters.format!;
      if (format === currentFormat) {
        return state;
      }
      return produce(state, (draft) => {
        if (!draft?.currentFavouriteSettings) {
          return state;
        }
        draft.currentFavouriteSettings.mark.parameters.format = format === BulkExportFormat.XLSX ? 0 : 1;
      });
    }
    case "setEmail": {
      const email = action.payload as string;
      if (email === state.email) {
        return state;
      }
      return produce(state, (draft) => {
        draft.email = email;
      });
    }

    case "setDimension": {
      const dimension = action.payload as IDimension;
      return produce(state, (draft) => {
        const existingDimensionIndex = draft.dimensions.findIndex((d) => d.id === dimension.id);
        if (existingDimensionIndex !== -1) {
          const existingDimension = draft.dimensions[existingDimensionIndex];
          if (Array.isArray(dimension.selection) && dimension.selection.length === 0) {
            draft.dimensions.splice(existingDimensionIndex, 1);
          } else {
            existingDimension.selection = dimension.selection;
            existingDimension.fieldName = dimension.fieldName;
            existingDimension.isSelected = !!dimension.fieldName;
          }
        } else {
          draft.dimensions.push(dimension);
        }
      });
    }

    case "setDimensionCurrentFavourite": {
      const dimension = action.payload as IDimension;
      return produce(state, (draft) => {
        if (!draft?.currentFavouriteSettings) {
          return state;
        }
        const existingDimensionIndex = draft?.currentFavouriteSettings.mark.parameters.dimensions.findIndex((d) => d.id === dimension.id);
        if (existingDimensionIndex !== -1) {
          const existingDimension = draft?.currentFavouriteSettings.mark.parameters.dimensions[existingDimensionIndex];
          if (dimension.selection.length === 0) {
            draft?.currentFavouriteSettings.mark.parameters.dimensions.splice(existingDimensionIndex, 1);
          } else {
            existingDimension.selection = dimension.selection;
            existingDimension.fieldName = dimension.fieldName;
            existingDimension.isSelected = !!dimension.fieldName;
          }
        } else if (dimension.selection.length !== 0) {
          draft?.currentFavouriteSettings.mark.parameters.dimensions.push(dimension);
        }
      });
    }
    case "setActionAfterSaveCancel":
      const actionAfterSaveCancel = action.payload as (() => void) | undefined;
      return produce(state, (draft) => {
        draft.actionAfterSaveCancel = actionAfterSaveCancel;
      });
    case "setSettingsWasModified":
      const settingsWasModifiedState = action.payload as boolean;
      return produce(state, (draft) => {
        draft.settingsWasModified = settingsWasModifiedState;
      });
    case "setSettingsModifiedForFavourites":
      const settingsModifiedForFavourites = action.payload as boolean;
      return produce(state, (draft) => {
        draft.settingsModifiedForFavourites = settingsModifiedForFavourites;
      });
    case "setShowSaveNotification":
      const setShowSaveNotification = action.payload as boolean;
      return produce(state, (draft) => {
        draft.showSaveNotification = setShowSaveNotification;
      });
    case "setDefaultDimensions":
      return produce(state, (draft) => {
        draft.dimensions = [];
      });
    case "setCurrentSavedSettings":
      const settings = action.payload as IsavedExportSettingsDTO;
      return produce(state, (draft) => {
        draft.currentSavedSettings = settings;
      });
    case "setCurrentFavouriteSettings":
      const settingsFavourite = action.payload as FavouriteBulkExportItem;
      return produce(state, (draft) => {
        draft.currentFavouriteSettings = settingsFavourite;
      });
    case "setDefaultCalendarDimension":
      const defaultCalendarDimension = action.payload as IDimension;
      return produce(state, (draft) => {
        draft.defaultCalendarDimension = defaultCalendarDimension;
      });
    default:
      throw new Error("Unknown command for reducer");
  }
}
